import React, { useState, useRef, useEffect } from 'react';
import { CheckCircleIcon } from 'lucide-react';

interface OTPInputProps {
  length?: number;
  onComplete: (code: string) => void;
  onResend?: () => void;
  title?: string;
  description?: string;
  resendText?: string;
  verifyText?: string;
  primaryColor?: string;
  isLoading?: boolean;
  disabled?: boolean;
  isResending?: boolean;
}

function hexToRgb(color: string): { r: number; g: number; b: number } | null {
  if (!color) return null;
  let hex = color.trim();
  if (hex.startsWith('#')) hex = hex.slice(1);
  if (hex.length === 3) {
    const r = parseInt(hex[0] + hex[0], 16);
    const g = parseInt(hex[1] + hex[1], 16);
    const b = parseInt(hex[2] + hex[2], 16);
    return { r, g, b };
  }
  if (hex.length === 6) {
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return { r, g, b };
  }
  return null;
}

function isColorLight(color: string | undefined): boolean {
  if (!color) return false;
  const rgb = hexToRgb(color);
  if (!rgb) return false;
  const { r, g, b } = rgb;
  // Perceived luminance formula
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
  return luminance > 186; // threshold
}

const OTPInput: React.FC<OTPInputProps> = ({
  length = 6,
  onComplete,
  onResend,
  title = 'Verification Code',
  description = 'Enter the verification code sent to your device',
  resendText = "Didn't receive a code? Resend",
  verifyText = 'Verify',
  primaryColor = '#2196f3',
  isLoading = false,
  disabled = false,
  isResending = false
}) => {
  const [code, setCode] = useState<string[]>(Array(length).fill(''));
  const [shouldFocusNext, setShouldFocusNext] = useState<number | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const useDarkTextOnButton = isColorLight(primaryColor);
  const buttonTextColorClass = useDarkTextOnButton ? 'text-gray-900' : 'text-white';
  const resendLinkColor = useDarkTextOnButton ? primaryColor : '#93c5fd';

  // Initialize refs array
  useEffect(() => {
    inputRefs.current = Array(length).fill(null);
  }, [length]);

  // Handle focus after state update
  useEffect(() => {
    if (shouldFocusNext !== null && inputRefs.current[shouldFocusNext]) {
      // Small delay to ensure DOM has updated
      setTimeout(() => {
        inputRefs.current[shouldFocusNext]?.focus();
      }, 0);
      setShouldFocusNext(null);
    }
  }, [shouldFocusNext]);

  const handleChange = (index: number, value: string) => {
    // Allow only numbers
    if (value && !/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-focus next input if current input is filled
    if (value && index < length - 1) {
      setShouldFocusNext(index + 1);
    }

    // Check if all inputs are filled
    if (newCode.every(digit => digit !== '') && newCode.join('').length === length) {
      onComplete(newCode.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    // Move to previous input on backspace if current input is empty
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();

    // Check if pasted content is a valid number with correct length
    if (/^\d+$/.test(pastedData) && pastedData.length === length) {
      const digits = pastedData.split('');
      setCode(digits);
      // Focus the last input
      inputRefs.current[length - 1]?.focus();
      onComplete(pastedData);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const fullCode = code.join('');
    if (fullCode.length === length) {
      onComplete(fullCode);
    }
  };

  const isCodeComplete = code.every(digit => digit !== '') && code.join('').length === length;

  return (
    <div className="w-full max-w-md">
      <h2 className="text-2xl font-bold mb-6 text-center text-gray-900 dark:text-gray-100">
        {title}
      </h2>

      <p className="text-gray-600 dark:text-gray-300 text-center mb-6">
        {description}
      </p>

      <form onSubmit={handleSubmit}>
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Verification Code
          </label>
          <div className="flex justify-between gap-2">
            {code.map((digit, index) => (
              <input
                key={index}
                ref={(el) => { inputRefs.current[index] = el; }}
                type="tel"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : undefined}
                disabled={disabled}
                className="w-12 h-12 text-center text-xl border border-gray-300 dark:border-gray-700 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:cursor-not-allowed dark:bg-gray-800 dark:text-gray-100"
              />
            ))}
          </div>

          {onResend && (
            <div className="mt-2 text-center">
              <button
                type="button"
                onClick={onResend}
                disabled={disabled || isResending}
                className="text-sm font-medium text-blue-600 hover:text-blue-500 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center justify-center mx-auto"
                style={{ color: resendLinkColor }}
              >
                {isResending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                    Sending...
                  </>
                ) : (
                  resendText
                )}
              </button>
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={disabled || isLoading || !isCodeComplete}
          className={`w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium ${buttonTextColorClass} bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed`}
          style={{ backgroundColor: primaryColor }}
        >
          {isLoading ? (
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current"></div>
          ) : (
            <>
              <CheckCircleIcon className="mr-2 h-5 w-5" />
              {verifyText}
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default OTPInput;
